<template>
  <div class="event-management-wrap">
    <!-- 页头 -->
    <div class="header-container">
      <div class="header-title">服务端事件</div>
      <el-button type="primary" @click="handleAdd">+ 新增事件</el-button>
    </div>

    <!-- 筛选 + 批量操作栏 -->
    <div class="filter-bar">
      <product-select
        class="filter-product-select"
        multiple
        :value.sync="filterForm.productIds"
        placeholder="选择产品"
        :isDefault="false"
        size="mini"
        popper-class="BI-select BI-select-product"
        :apiName="getFilterProductList"
        @change="handleSearch"
      ></product-select>
      <el-input
        v-model="filterForm.eventName"
        class="filter-event-name"
        placeholder="搜索事件名称"
        clearable
        @keyup.enter.native="handleSearch"
      ></el-input>
      <el-select
        v-model="filterForm.eventTypes"
        style="width: 200px; margin-left: 8px"
        placeholder="全部事件类型"
        clearable
        multiple
        collapse-tags
      >
        <el-option
          v-for="item in EVENT_TYPE_OPTIONS"
          :key="item.value"
          :label="item.label"
          :value="item.value"
        ></el-option>
      </el-select>
      <el-select v-model="filterForm.status" style="width: 120px; margin-left: 8px" placeholder="全部状态" clearable>
        <el-option label="全部" value=""></el-option>
        <el-option label="启用中" value="1"></el-option>
        <el-option label="禁用" value="0"></el-option>
      </el-select>
      <el-button type="primary" style="margin-left: 8px" @click="handleSearch">查询</el-button>
      <el-button style="margin-left: 8px" @click="handleReset">重置</el-button>

      <!-- 批量操作（勾选 ≥1 行时展示） -->
      <template v-if="selectedRows.length > 0">
        <span class="selected-count">已选择 {{ selectedRows.length }} 项</span>
        <el-button type="text" class="batch-btn" @click="handleBatchCopy">批量复制</el-button>
        <el-button type="text" class="batch-btn batch-delete-btn" @click="handleBatchDelete">批量删除</el-button>
        <el-button type="text" class="batch-btn" @click="handleCancelSelection">取消选择</el-button>
      </template>
    </div>

    <!-- 数据表格 -->
    <el-table
      :data="list"
      v-loading="loading"
      element-loading-text="拼命加载中"
      element-loading-spinner="el-icon-loading"
      element-loading-background="rgba(0, 0, 0, 0.1)"
      ref="table"
      class="event-table"
      height="100%"
      :default-sort="{ prop: 'updateTime', order: 'descending' }"
      @sort-change="handleSortChange"
      @selection-change="handleSelectionChange"
    >
      <el-table-column type="selection" width="50" align="center" :selectable="() => true"></el-table-column>
      <el-table-column
        label="产品名称"
        prop="productName"
        min-width="150"
        sortable="custom"
        show-overflow-tooltip
        :formatter="emptyFormatter"
      ></el-table-column>
      <el-table-column
        label="事件名称"
        prop="eventName"
        min-width="160"
        sortable="custom"
        show-overflow-tooltip
        :formatter="emptyFormatter"
      ></el-table-column>
      <el-table-column
        label="事件类型"
        prop="eventType"
        min-width="160"
        sortable="custom"
        show-overflow-tooltip
        :formatter="emptyFormatter"
      ></el-table-column>
      <el-table-column
        label="备注"
        prop="notes"
        min-width="180"
        sortable="custom"
        show-overflow-tooltip
        :formatter="emptyFormatter"
      ></el-table-column>
      <el-table-column
        label="操作人"
        prop="creator"
        min-width="100"
        sortable="custom"
        :formatter="emptyFormatter"
      ></el-table-column>
      <el-table-column
        label="更新时间"
        prop="updateTime"
        width="180"
        sortable="custom"
        :formatter="emptyFormatter"
      ></el-table-column>
      <el-table-column label="状态" prop="event_status" width="140" align="center" sortable="custom">
        <template slot-scope="scope">
          <div class="status-cell">
            <el-switch
              v-model="scope.row.event_status"
              :active-value="1"
              :inactive-value="0"
              @change="handleStatusChange(scope.row)"
            ></el-switch>
            <span :class="['status-label', scope.row.event_status == 1 ? 'is-active' : 'is-disabled']">
              {{ scope.row.event_status == 1 ? '启用中' : '禁用' }}
            </span>
          </div>
        </template>
      </el-table-column>
      <el-table-column label="操作" width="160" align="center">
        <template slot-scope="scope">
          <div class="action-btn-wrap">
            <el-button type="text" class="action-btn" @click="handleEdit(scope.row)">编辑</el-button>
            <el-button type="text" class="action-btn" @click="openCopyDialog([scope.row])">复制</el-button>
            <el-button type="text" class="action-btn delete-btn" @click="doDelete([scope.row.id])">删除</el-button>
          </div>
        </template>
      </el-table-column>
    </el-table>

    <self-pagination
      class="footer-page"
      :total="pageData.total"
      :pageSize="pageData.size"
      :currentPage="pageData.page"
      @getCurrentPage="changePage"
      @handleSizeChange="handleSizeChange"
    ></self-pagination>

    <!-- 复制弹窗 -->
    <el-dialog
      :title="copySourceRows.length > 1 ? '批量复制' : '复制'"
      :visible.sync="copyDialogVisible"
      width="440px"
      :close-on-click-modal="false"
      @close="closeCopyDialog"
    >
      <el-form label-width="80px" style="padding-right: 16px">
        <el-form-item label="目标产品">
          <product-select
            :value.sync="copyTargetProductId"
            placeholder="请选择复制到产品"
            :isDefault="false"
            popper-class="BI-select BI-select-product"
          ></product-select>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="closeCopyDialog">取消</el-button>
        <el-button type="primary" :disabled="!copyTargetProductId" :loading="copyLoading" @click="submitCopy">
          确定
        </el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import {
  getBuyEventMergedPageList,
  getBuyEventMergedProductList,
  deleteBuyEventMerged,
  updateBuyEventMergedStatus,
  batchCopyBuyEvent,
  batchDeleteBuyEventMerged,
} from '@/api/eventManager'

const EVENT_TYPE_OPTIONS = [
  { label: '关卡', value: '关卡' },
  { label: '激励视频', value: '激励视频' },
  { label: '插屏', value: '插屏' },
  { label: '广告收入', value: '广告收入' },
  { label: '内购收入', value: '内购收入' },
  { label: '有效登录间隔', value: '有效登录间隔' },
]

export default {
  name: 'AcquisitionEventManagement',

  data() {
    return {
      EVENT_TYPE_OPTIONS,
      filterForm: {
        productIds: [],
        eventName: '',
        eventTypes: [],
        status: '',
      },
      list: [],
      pageData: {
        total: 0,
        size: 20,
        page: 1,
      },
      loading: false,
      sortField: 'updateTime',
      sortOrder: 'desc',
      suppressSortChange: false,
      selectedRows: [],
      // 复制弹窗
      copyDialogVisible: false,
      copyTargetProductId: null,
      copySourceRows: [],
      copyLoading: false,
    }
  },

  mounted() {
    this.searchData()
  },

  methods: {
    async getFilterProductList(params = {}) {
      const res = await getBuyEventMergedProductList({
        keyword: params.keyword || '',
      })
      return {
        ...res,
        products: (res.data || []).map(item => ({
          ...item,
          storeName: item.pkg || '',
        })),
      }
    },

    async searchData() {
      try {
        this.loading = true
        const params = {
          productIds: this.filterForm.productIds.length ? this.filterForm.productIds : undefined,
          eventName: this.filterForm.eventName,
          eventTypes: this.filterForm.eventTypes.length ? this.filterForm.eventTypes : undefined,
          pageNum: this.pageData.page,
          pageSize: this.pageData.size,
          orderField: this.sortField,
          orderType: this.sortOrder,
        }
        if (this.filterForm.status !== '') {
          params.status = this.filterForm.status
        }
        const res = await getBuyEventMergedPageList(params)
        if (res.code === '0000') {
          this.list = (res.data.list || []).map(item => ({
            ...item,
            event_status:
              item.event_status !== undefined && item.event_status !== null ? item.event_status : item.status,
          }))
          this.pageData.total = res.data.total || 0
        } else {
          this.$message.error(res.message || '获取列表数据失败')
        }
      } catch (error) {
        console.error('获取列表数据失败:', error)
        this.$message.error('获取列表数据失败')
      } finally {
        this.loading = false
      }
    },

    handleSearch() {
      this.pageData.page = 1
      this.searchData()
    },

    handleReset() {
      this.filterForm = { productIds: [], eventName: '', eventTypes: [], status: '' }
      this.sortField = 'updateTime'
      this.sortOrder = 'desc'
      this.pageData.page = 1
      // 恢复表头排序指示器，suppress 防止 sort-change 触发重复请求
      this.suppressSortChange = true
      this.$nextTick(() => {
        if (this.$refs.table) {
          this.$refs.table.sort('updateTime', 'descending')
        }
        this.$nextTick(() => {
          this.suppressSortChange = false
        })
      })
      this.searchData()
    },

    handleSortChange({ prop, order }) {
      if (this.suppressSortChange) return
      if (!order) {
        // el-table 三态循环到 null 时，重置为默认排序
        this.sortField = 'updateTime'
        this.sortOrder = 'desc'
      } else {
        this.sortField = prop
        this.sortOrder = order === 'ascending' ? 'asc' : 'desc'
      }
      this.pageData.page = 1
      this.searchData()
    },

    handleSelectionChange(rows) {
      this.selectedRows = rows
    },

    handleCancelSelection() {
      this.$refs.table && this.$refs.table.clearSelection()
    },

    // ---- CRUD ----

    handleAdd() {
      this.$router.push({
        name: 'AcquisitionEventManagementAdd',
        query: { mode: 'add' },
      })
    },

    handleEdit(row) {
      this.$router.push({
        name: 'AcquisitionEventManagementAdd',
        query: { mode: 'edit', id: row.id },
      })
    },

    doDelete(ids) {
      this.$confirm('删除后不可恢复，确定要删除吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning',
      })
        .then(async () => {
          try {
            let res
            if (ids.length === 1) {
              res = await deleteBuyEventMerged({ id: ids[0] })
            } else {
              res = await batchDeleteBuyEventMerged({ ids })
            }
            if (res.code === '0000') {
              this.$message.success('删除成功')
              if (this.list.length <= ids.length && this.pageData.page > 1) {
                this.pageData.page--
              }
              this.handleCancelSelection()
              this.searchData()
            } else {
              this.$message.error(res.message || '删除失败')
            }
          } catch (e) {
            console.error('删除失败:', e)
            this.$message.error('删除失败')
          }
        })
        .catch(() => {})
    },

    handleBatchDelete() {
      const ids = this.selectedRows.map(r => r.id)
      this.doDelete(ids)
    },

    // ---- 复制弹窗 ----

    openCopyDialog(rows) {
      this.copySourceRows = rows
      this.copyTargetProductId = null
      this.copyDialogVisible = true
    },

    handleBatchCopy() {
      this.openCopyDialog(this.selectedRows)
    },

    closeCopyDialog() {
      this.copyDialogVisible = false
      this.copySourceRows = []
      this.copyTargetProductId = null
    },

    async submitCopy() {
      if (!this.copyTargetProductId) return
      try {
        this.copyLoading = true
        const ids = this.copySourceRows.map(r => r.id)
        const res = await batchCopyBuyEvent({
          ids,
          targetProductId: this.copyTargetProductId,
        })
        if (res.code === '0000') {
          const { success = 0, fail = 0 } = res.data || {}
          this.closeCopyDialog()
          this.searchData()

          if (fail > 0) {
            const msg =
              this.copySourceRows.length > 1
                ? `复制完成：成功 ${success} 项，失败 ${fail} 项（目标产品下已存在同名事件）`
                : '目标产品下已存在同名事件，复制失败'
            this.$alert(msg, '提示', {
              confirmButtonText: '确定',
              type: success > 0 ? 'warning' : 'error',
            })
          } else {
            this.$message.success('复制成功')
          }
        } else {
          // 同名事件冲突等需要强提示（modal）
          this.$alert(res.message || '复制失败', '提示', {
            confirmButtonText: '确定',
            type: 'error',
          })
        }
      } catch (e) {
        console.error('复制失败:', e)
        this.$alert('复制失败，请重试', '提示', {
          confirmButtonText: '确定',
          type: 'error',
        })
      } finally {
        this.copyLoading = false
      }
    },

    // ---- 状态切换 ----

    async handleStatusChange(row) {
      const statusText = row.event_status === 1 ? '启用' : '禁用'
      try {
        const res = await updateBuyEventMergedStatus({ id: row.id, status: row.event_status })
        if (res.code === '0000') {
          this.$message.success(`${statusText}成功`)
        } else {
          this.$message.error(res.message || `${statusText}失败`)
          row.event_status = row.event_status === 1 ? 0 : 1
        }
      } catch (e) {
        console.error('修改状态失败:', e)
        this.$message.error(`${statusText}失败`)
        row.event_status = row.event_status === 1 ? 0 : 1
      }
    },

    emptyFormatter(row, column, cellValue) {
      return cellValue !== null && cellValue !== undefined && cellValue !== '' ? cellValue : '-'
    },

    // ---- 分页 ----

    changePage(page) {
      this.pageData.page = page
      this.searchData()
    },

    handleSizeChange(size) {
      this.pageData.size = size
      this.pageData.page = 1
      this.searchData()
    },
  },
}
</script>

<style lang="less" scoped>
.event-management-wrap {
  margin: 16px;
  height: calc(100vh - 124px);
  padding: 16px 24px;
  border-radius: 4px;
  background: #fff;
  box-shadow: 0px 2px 12px rgba(0, 0, 0, 0.12);
  display: flex;
  flex-direction: column;

  .header-container {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;
    flex-shrink: 0;

    .header-title {
      color: rgba(0, 0, 0, 0.85);
      font-weight: bold;
      font-size: 15px;
    }
  }

  .filter-bar {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0;
    margin-bottom: 16px;
    flex-shrink: 0;

    .filter-product-select {
      min-width: 160px;
      margin-left: 0;
    }

    .filter-event-name {
      width: 160px;
      margin-left: 8px;
    }

    .filter-product-select,
    .filter-event-name {
      height: 32px;
    }

    .filter-product-select {
      :deep(.product-multi-select) {
        width: 100%;
      }

      :deep(.el-select),
      :deep(.el-input),
      :deep(.el-input__inner) {
        height: 32px;
      }

      :deep(.el-input__inner) {
        line-height: 32px;
      }

      :deep(.el-select__tags) {
        top: 50%;
        display: flex;
        align-items: center;
        flex-wrap: nowrap;
        max-width: calc(100% - 30px);
        white-space: nowrap;
        overflow: hidden;
        transform: translateY(-50%);
      }

      :deep(.el-tag.el-tag--info) {
        flex: 0 1 auto;
        max-width: calc(100% - 42px);
        margin-top: 0;
        margin-bottom: 0;
        overflow: hidden;
      }

      :deep(.el-tag.el-tag--info:first-child) {
        min-width: 0;
        max-width: calc(100% - 48px);
      }

      :deep(.el-tag.el-tag--info + .el-tag.el-tag--info) {
        flex: 0 0 auto;
        min-width: 38px;
        max-width: 54px;
        margin-left: 4px;
        padding: 0 6px;
        text-align: center;
        box-sizing: border-box;
      }

      :deep(.el-select__tags-text) {
        display: inline-block;
        max-width: calc(100% - 16px);
        overflow: hidden;
        text-overflow: ellipsis;
        vertical-align: middle;
      }

      :deep(.el-tag.el-tag--info + .el-tag.el-tag--info .el-select__tags-text) {
        max-width: none;
        overflow: visible;
        text-overflow: clip;
      }

      :deep(.el-input__icon) {
        line-height: 32px;
      }
    }

    .selected-count {
      margin-left: 16px;
      font-size: 13px;
      color: rgba(0, 0, 0, 0.65);
      white-space: nowrap;
    }

    .batch-btn {
      margin-left: 8px;
      font-size: 13px;
      padding: 0 4px;
      color: #1890ff;

      &.batch-delete-btn {
        color: #ff4d4f;
      }
    }
  }

  .event-table {
    flex: 1;
    min-height: 0;

    .status-cell {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;

      .status-label {
        font-size: 13px;
        white-space: nowrap;

        &.is-active {
          color: #52c41a;
        }

        &.is-disabled {
          color: rgba(0, 0, 0, 0.45);
        }
      }
    }

    .action-btn-wrap {
      display: flex;
      align-items: center;
      justify-content: center;

      .el-button + .el-button {
        margin-left: 4px;
      }
    }

    .action-btn {
      padding: 0 4px;
      font-size: 13px;
      color: #1890ff;

      &.delete-btn {
        color: #ff4d4f;
      }
    }
  }

  .footer-page {
    margin-top: 12px;
    text-align: right;
    flex-shrink: 0;
  }
}

::v-deep .el-button--primary {
  background-color: #1890ff;
  border-color: #1890ff;

  &:hover {
    background-color: #40a9ff;
    border-color: #40a9ff;
  }
}

::v-deep .el-switch.is-checked .el-switch__core {
  background-color: #1890ff;
  border-color: #1890ff;
}

::v-deep .el-table th {
  background-color: #fafafa;
  color: rgba(0, 0, 0, 0.85);
  font-weight: 500;
}

::v-deep .el-table {
  font-size: 13px;
  color: rgba(0, 0, 0, 1);
}

::v-deep .el-table .el-table__row:hover td {
  background-color: #e6f7ff;
}
</style>
